import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { orgApi, type Org, type OrgProject, type ProjectCreated } from "../api/orgApi";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import CustomButton from "../components/CustomButton";

export function OrgDetailPage() {
  const { orgId = "" } = useParams<{ orgId: string }>();
  const nav = useNavigate();

  const [org, setOrg] = useState<Org | null>(null);
  const [projects, setProjects] = useState<OrgProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // create project
  const [showCreate, setShowCreate] = useState(false);
  const [projName, setProjName] = useState("");
  const [projDesc, setProjDesc] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [createdProject, setCreatedProject] = useState<ProjectCreated | null>(null);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const [orgs, projs] = await Promise.all([
          orgApi.listOrgs(),
          orgApi.listProjects(orgId),
        ]);
        setOrg(orgs.find((o) => o.id === orgId) ?? null);
        setProjects(projs);
      } catch (e) {
        setErr((e as Error).message);
      } finally {
        setLoading(false);
      }
    })();
  }, [orgId]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    if (!projName.trim()) return;
    setCreateBusy(true);
    setErr(null);
    try {
      const p = await orgApi.createProject(orgId, projName.trim(), projDesc.trim() || undefined);
      setCreatedProject(p);
      setProjects((prev) => [p, ...prev]);
      setProjName("");
      setProjDesc("");
      setShowCreate(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setCreateBusy(false);
    }
  }

  if (loading) {
    return (
      <Box className="orgs-page">
        <Typography variant="body2" className="login-subtitle">Loading…</Typography>
      </Box>
    );
  }

  return (
    <Box className="orgs-page">
      {/* Breadcrumb */}
      <Link to="/organizations" className="org-detail-back">
        <img src="/arrow_right.png" alt="" style={{ width: 14, height: 14, verticalAlign: "middle", marginRight: 4, transform: "scaleX(-1)" }} /> Organizations
      </Link>

      {/* Org header */}
      <Box className="orgs-header" sx={{ mt: 1 }}>
        <Box>
          <Typography variant="h5" className="orgs-page-title">
            {org?.name ?? orgId}
          </Typography>
          {org?.slug && (
            <Typography variant="body2" className="login-subtitle" sx={{ mt: 0.5, mb: 0 }}>
              <code className="orgs-slug">{org.slug}</code>
              {org.description && <span style={{ marginLeft: 8 }}>{org.description}</span>}
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1 }} />
        <CustomButton
          loading={false}
          buttonText={showCreate ? "Cancel" : "+ New project"}
          buttonTextLoading=""
          handleSubmitClick={() => { setShowCreate((v) => !v); setCreatedProject(null); }}
        />
      </Box>

      {/* Create project form */}
      {showCreate && (
        <Card className="orgs-create-card" sx={{ mb: 3 }}>
          <CardContent component="form" onSubmit={onCreate} className="login-card-content">
            <Typography variant="h6" className="login-title">
              Create project
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
              <TextField
                label="Project name"
                value={projName}
                onChange={(e) => setProjName(e.target.value)}
                placeholder="my-service"
                size="small"
                fullWidth
                autoFocus
                className="login-input"
              />
              <TextField
                label="Description (optional)"
                value={projDesc}
                onChange={(e) => setProjDesc(e.target.value)}
                placeholder="Short description"
                size="small"
                fullWidth
                className="login-input"
              />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
              <CustomButton
                loading={false}
                buttonText="Cancel"
                buttonTextLoading=""
                handleSubmitClick={() => setShowCreate(false)}
                background="transparent"
                color="rgba(74, 30, 71, 1)"
                border="1px solid rgba(74, 30, 71, 0.70)"
                hoverBgColor="rgba(74, 30, 71, 0.08)"
              />
              <CustomButton
                loading={createBusy}
                disabled={!projName.trim()}
                buttonText="Create project"
                buttonTextLoading="Creating…"
                handleSubmitClick={() => {}}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Created project success */}
      {createdProject && (
        <Alert
          severity="success"
          sx={{ mb: 3, borderRadius: 2, border: "1px solid rgba(74, 30, 71, 0.70)" }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Project created!
          </Typography>
          <Box component="table" sx={{ fontSize: 13, mb: 1 }}>
            <tbody>
              <tr>
                <td style={{ paddingRight: 16, color: "rgba(120,120,125,1)" }}>Name</td>
                <td>{createdProject.name}</td>
              </tr>
              <tr>
                <td style={{ paddingRight: 16, color: "rgba(120,120,125,1)" }}>Project ID</td>
                <td><code className="orgs-slug">{createdProject.id}</code></td>
              </tr>
              <tr>
                <td style={{ paddingRight: 16, color: "rgba(120,120,125,1)" }}>API key</td>
                <td><code className="orgs-slug">{createdProject.api_key}</code></td>
              </tr>
            </tbody>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            Save the API key — it won't be shown again.
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <CustomButton
              loading={false}
              buttonText={<>Open project <img src="/arrow_right.png" alt="" style={{ width: 14, height: 14, verticalAlign: "middle", marginLeft: 4 }} /></>}
              buttonTextLoading=""
              handleSubmitClick={() => nav(`/organizations/${orgId}/projects/${createdProject.id}`, { state: { apiKey: createdProject.api_key } })}
            />
            <CustomButton
              loading={false}
              buttonText="Dismiss"
              buttonTextLoading=""
              handleSubmitClick={() => setCreatedProject(null)}
              background="transparent"
              color="rgba(74, 30, 71, 1)"
              border="1px solid rgba(74, 30, 71, 0.70)"
              hoverBgColor="rgba(74, 30, 71, 0.08)"
            />
          </Box>
        </Alert>
      )}

      {/* Error */}
      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      {/* Projects list */}
      {projects.length === 0 ? (
        <Card className="orgs-create-card" sx={{ textAlign: "center", py: 6 }}>
          <CardContent>
            <Typography variant="body2" className="login-subtitle" sx={{ mb: 2 }}>
              No projects in this organization.
            </Typography>
            <CustomButton
              loading={false}
              buttonText="Create the first project"
              buttonTextLoading=""
              handleSubmitClick={() => setShowCreate(true)}
            />
          </CardContent>
        </Card>
      ) : (
        <Box>
          {/* View toggle */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="overline" className="org-detail-section-title" sx={{ mb: 0 }}>
              Projects ({projects.length})
            </Typography>
            <Box className="view-toggle">
              <button
                className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
              >
                ▦ Grid
              </button>
              <button
                className={`view-toggle-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
              >
                ≡ List
              </button>
            </Box>
          </Box>

          {viewMode === "grid" ? (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 2 }}>
              {projects.map((p) => {
                const initials = p.name
                  .split(/[\s_-]+/)
                  .map((w) => w[0]?.toUpperCase() ?? "")
                  .slice(0, 2)
                  .join("");
                const d = new Date(p.created_at);
                const day = d.getDate();
                const suffix = [11,12,13].includes(day) ? "th" : ({1:"st",2:"nd",3:"rd"} as Record<number,string>)[day % 10] || "th";
                const month = d.toLocaleString(undefined, { month: "short" });
                const year = d.getFullYear();
                let hours = d.getHours();
                const mins = d.getMinutes().toString().padStart(2, "0");
                const ampm = hours >= 12 ? "pm" : "am";
                hours = hours % 12 || 12;
                const dateStr = `${day}${suffix} ${month} ${year}, ${hours}:${mins} ${ampm}`;

                return (
                  <Link key={p.id} to={`/organizations/${orgId}/projects/${p.id}`} style={{ textDecoration: "none" }}>
                    <Card className="project-grid-card">
                      <CardContent sx={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 1, p: 2.5, "&:last-child": { pb: 2.5 } }}>
                        <Box className="project-grid-avatar">
                          {initials}
                        </Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "rgba(21, 0, 39, 1)", mt: 1 }}>
                          {p.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(120,120,125,1)" }}>
                          {p.description || "Greenfield"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "rgba(147,147,153,1)", fontSize: "11px" }}>
                          {dateStr}
                        </Typography>
                        <Typography sx={{ color: "rgba(120,120,125,1)", mt: 1, fontSize: "18px" }}><img src="/arrow_right.png" alt="Go" style={{ width: 18, height: 18 }} /></Typography>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </Box>
          ) : (
            <Card className="orgs-create-card">
              <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Created</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {projects.map((p) => {
                      const d = new Date(p.created_at);
                      const day = d.getDate();
                      const suffix = [11,12,13].includes(day) ? "th" : ({1:"st",2:"nd",3:"rd"} as Record<number,string>)[day % 10] || "th";
                      const month = d.toLocaleString(undefined, { month: "short" });
                      const year = d.getFullYear();
                      let hours = d.getHours();
                      const mins = d.getMinutes().toString().padStart(2, "0");
                      const ampm = hours >= 12 ? "pm" : "am";
                      hours = hours % 12 || 12;
                      const dateStr = `${day}${suffix} ${month} ${year}, ${hours}:${mins} ${ampm}`;

                      return (
                        <tr key={p.id} style={{ cursor: "pointer" }} onClick={() => nav(`/organizations/${orgId}/projects/${p.id}`)}>
                          <td style={{ fontWeight: 500 }}>{p.name}</td>
                          <td style={{ color: "rgba(120,120,125,1)" }}>{p.description || "—"}</td>
                          <td style={{ color: "rgba(147,147,153,1)" }}>{dateStr}</td>
                          <td><Link to={`/organizations/${orgId}/projects/${p.id}`}><img src="/arrow_right.png" alt="Go" style={{ width: 18, height: 18 }} /></Link></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </Box>
      )}
    </Box>
  );
}
